import type { ArticleContent } from "./types";

export const articlePt: Record<string, ArticleContent> = {
  "basetao-spreadsheet-guide": {
    slug: "basetao-spreadsheet-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20clean%20workspace%20flat%20lay%20with%20a%20laptop%20showing%20a%20colorful%20spreadsheet%20grid%2C%20a%20notebook%2C%20a%20coffee%20cup%20and%20a%20folded%20sage%20green%20sweater%20on%20a%20warm%20cream%20desk%2C%20soft%20natural%20light%2C%20airy%20editorial%20photography%2C%20fresh%20minimal%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-guide&orientation=landscape&nocache=true",
    title: "Basetao Spreadsheet: guia completo de produtos, vendedores e compras (2026)",
    cardDescription: "Como a planilha da Basetao funciona, como as categorias são organizadas e como encontrar vendedores confiáveis.",
    metaDescription: "Aprenda como a planilha da Basetao funciona, explore categorias, encontre vendedores confiáveis e entenda o envio e o controle de qualidade.",
    eyebrow: "Guia de compra",
    intro: "A planilha da Basetao é um diretório compartilhado que reúne links de produtos, detalhes de vendedores e notas de compra em um só lugar. Este guia explica sua organização, leitura e uso para comprar com confiança.",
    sections: [
      {
        heading: "O que é a planilha da Basetao?",
        paragraphs: [
          "A planilha da Basetao é uma lista selecionada e organizada de produtos e vendedores usada por quem compra em marketplaces internacionais. Em vez de navegar sem fim, abre-se a planilha para encontrar links diretos, atalhos de categorias e notas de vendedores em uma única visão.",
          "Ela funciona como ponte entre a busca e a compra real. Cada entrada costuma apontar para uma página de produto ativa no site principal.",
        ],
      },
      {
        heading: "Como as categorias são organizadas",
        paragraphs: [
          "A planilha agrupa produtos em categorias claras como Calçados, Moletons e Suéteres, Camisetas, Jaquetas, Calças e Shorts, Bonés, Conjuntos, Roupa íntima, Camisas esportivas, Acessórios, Bolsas, Cintos e Perfumes.",
          "Cada categoria leva a uma visualização filtrada no site principal, para ir de uma ideia ampla a uma lista concreta em um clique.",
        ],
      },
      {
        heading: "Como encontrar vendedores confiáveis",
        paragraphs: [
          "Procure vendedores com fichas completas, notas de tamanho claras e fotos realistas. Entradas confiáveis costumam incluir um link para a página do produto, uma nota de tamanho ou material e uma data de atualização.",
          "Se uma ficha parecer vaga ou sem detalhes, trate com cautela. As entradas mais úteis ajudam a verificar um produto antes de se comprometer.",
        ],
      },
      {
        heading: "Noções básicas de envio e controle de qualidade",
        paragraphs: [
          "Antes de comprar, confira a tabela de tamanhos, a descrição do material e as avaliações disponíveis. Em roupas, compare as medidas informadas com as suas.",
          "Tempos e custos de envio variam por item e destino. Confirme sempre o preço final, o prazo estimado e a política de devolução antes de concluir.",
        ],
      },
    ],
    comparisonTitle: "Referência rápida de categorias",
    comparisonHeaders: ["Categoria", "O que conferir", "Ideal para"],
    comparisonRows: [
      ["Calçados", "Tamanho, sola, cores", "Dia a dia e streetwear"],
      ["Moletons / Suéteres", "Material, peso, caimento", "Looks casuais e em camadas"],
      ["Camisetas", "Tecido, qualidade da estampa, caimento", "Básicas e gráficas"],
      ["Jaquetas", "Isolamento, impermeabilidade, tamanho", "Casacos e estações"],
      ["Acessórios e bolsas", "Material, alça, capacidade", "Toques finais"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "A planilha da Basetao é gratuita?", a: "Sim. É um diretório informativo e as páginas vinculadas estão no site principal." },
      { q: "Preciso de uma conta para usar?", a: "Não. Navegar por categorias e ler guias não exige conta. As compras são feitas no site principal." },
      { q: "Com que frequência a planilha é atualizada?", a: "As entradas são revisadas regularmente, mas disponibilidade e preços podem mudar. Verifique sempre na página ativa." },
      { q: "Este site é afiliado aos vendedores?", a: "É um recurso informativo independente que organiza links públicos sem substituir as políticas do site principal." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-review": {
    slug: "basetao-review",
    image:
      "https://readdy.ai/api/search-image?query=A%20modern%20desk%20scene%20with%20a%20tablet%20displaying%20a%20product%20listing%20and%20a%20small%20star%20rating%20graphic%20printed%20on%20paper%20beside%20a%20coffee%20cup%2C%20warm%20terracotta%20accents%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-review&orientation=landscape",
    title: "Análise da Basetao: envio, armazém, controle de qualidade e segurança",
    cardDescription: "Um olhar honesto sobre o envio, armazém, controle de qualidade e segurança da Basetao — o que funciona e o que observar.",
    metaDescription: "Uma análise honesta da Basetao sobre opções de envio, gestão do armazém, controle de qualidade e segurança do comprador.",
    eyebrow: "Análise",
    intro: "Esta análise percorre os aspectos da Basetao que mais importam — envio, armazém, controle de qualidade e segurança — para você decidir se combina com seu jeito de comprar.",
    sections: [
      {
        heading: "Envio: opções e o que esperar",
        paragraphs: [
          "A Basetao oferece vários métodos de envio que variam por destino, rapidez e custo. Serviços expressos custam mais, mas incluem rastreio detalhado; opções econômicas são mais baratas e lentas.",
          "A melhor escolha depende da urgência e do orçamento. Revise sempre o prazo estimado antes de finalizar o pedido.",
        ],
      },
      {
        heading: "Armazém e processamento do pedido",
        paragraphs: [
          "O armazém é onde seus itens são recebidos, conferidos e preparados para o envio. Um armazém bem gerido mantém o pedido junto e confirma que o comprado coincide com o enviado.",
          "Atualizações claras reduzem surpresas. Se um item chegar danificado ou diferente, as notas e fotos do armazém servem de base para reembolso ou troca.",
        ],
      },
      {
        heading: "Controle de qualidade e segurança",
        paragraphs: [
          "O controle de qualidade ajuda a detectar problemas evidentes antes do envio, mas não é garantia de perfeição. Fotos e listas ajudam, embora pequenos defeitos possam passar.",
          "Por segurança, pague com métodos reconhecidos, guarde os registros do pedido e verifique os dados do vendedor antes de compras grandes.",
        ],
      },
    ],
    comparisonTitle: "Basetao em resumo",
    comparisonHeaders: ["Área", "O que funciona bem", "O que observar"],
    comparisonRows: [
      ["Envio", "Vários métodos e rastreio", "Custo e tempo por método"],
      ["Armazém", "Consolidação e conferências", "Depende do estado na chegada"],
      ["Controle de qualidade", "Fotos e listas de verificação", "Não é garantia de perfeição"],
      ["Segurança", "Métodos de pagamento reconhecidos", "Verifique sempre os vendedores"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "A Basetao é um serviço confiável?", a: "Muitos compradores usam com sucesso, mas como qualquer marketplace depende dos vendedores e itens. Pesquisar reduz o risco." },
      { q: "A Basetao inspeciona cada item?", a: "As conferências e o controle de qualidade ajudam, mas não garantem que cada item seja perfeito." },
      { q: "Posso rastrear meu pedido?", a: "A maioria dos métodos inclui rastreio. Escolha um serviço rastreável para acompanhar o pacote até a porta." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "is-basetao-legit": {
    slug: "is-basetao-legit",
    image:
      "https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20resting%20over%20a%20printed%20document%20on%20a%20clean%20desk%20with%20a%20small%20padlock%20and%20a%20pen%2C%20soft%20peach%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-legit&orientation=landscape",
    title: "A Basetao é legítima? Segurança, controle de qualidade e guia de compra",
    cardDescription: "Uma resposta clara sobre se a Basetao é legítima, mais os passos de segurança e controle de qualidade que protegem seu dinheiro.",
    metaDescription: "A Basetao é legítima? Analisamos segurança, controle de qualidade e proteção do comprador para você comprar com confiança.",
    eyebrow: "Verificação de segurança",
    intro: "Uma pergunta frequente é se a Basetao é legítima. A resposta curta é que é um serviço real muito usado, mas sua segurança depende de como você compra.",
    sections: [
      {
        heading: "O que torna um serviço legítimo",
        paragraphs: [
          "Um serviço legítimo tem políticas claras, suporte real e opções de envio e pagamento transparentes. A Basetao as oferece, mas a qualidade de vendedores e fichas varia.",
          "A legitimidade da plataforma não torna automaticamente cada produto ou vendedor confiável. Trate cada compra como uma decisão própria.",
        ],
      },
      {
        heading: "Como proteger seu dinheiro",
        paragraphs: [
          "Use métodos de pagamento reconhecidos com proteção ao comprador e guarde cada pedido, mensagem e recibo. Evite pagar fora da plataforma ou por vias não rastreáveis.",
          "Se uma oferta parece boa demais, geralmente é. Compare preços e leia a descrição antes de pagar.",
        ],
      },
      {
        heading: "O papel do controle de qualidade",
        paragraphs: [
          "O controle de qualidade é uma camada útil que confere os itens em busca de problemas evidentes antes do envio. Reduz — sem eliminar — o risco de receber um produto errado ou danificado.",
          "Peça fotos de controle de qualidade quando disponíveis e informe qualquer divergência assim que o pedido chegar.",
        ],
      },
    ],
    comparisonTitle: "Lista de verificação de legitimidade",
    comparisonHeaders: ["Conferência", "Por que importa", "O que fazer"],
    comparisonRows: [
      ["Método de pagamento", "Proteção ao comprador", "Usar opções reconhecidas"],
      ["Dados do vendedor", "Confiança e responsabilidade", "Ler fichas e avaliações"],
      ["Fotos de controle", "Detecta problemas visíveis", "Pedir quando possível"],
      ["Registros do pedido", "Suporte em disputas", "Guardar recibos e mensagens"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "A Basetao é um golpe?", a: "Não. A Basetao é uma plataforma legítima, mas você deve pesquisar vendedores e produtos antes de comprar." },
      { q: "E se meu pedido nunca chegar?", a: "Contate o suporte com os dados e o rastreio. Métodos de pagamento reconhecidos costumam oferecer proteção extra." },
      { q: "Devo pedir fotos de controle de qualidade?", a: "Sim. Elas ajudam a detectar problemas evidentes antes do envio e servem de prova se algo falhar." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-shipping-cost": {
    slug: "basetao-shipping-cost",
    image:
      "https://readdy.ai/api/search-image?query=A%20stack%20of%20cardboard%20shipping%20boxes%20with%20a%20shipping%20label%20and%20a%20measuring%20tape%20on%20a%20warm%20sand%20table%2C%20soft%20natural%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-shipping-cost&orientation=landscape",
    title: "Custo de envio da Basetao: guia completo e comparação",
    cardDescription: "Como os custos de envio da Basetao são calculados, os métodos disponíveis e como escolher a opção confiável mais barata.",
    metaDescription: "Um guia completo dos custos de envio da Basetao — como são calculados, os métodos disponíveis e como escolher a opção mais barata.",
    eyebrow: "Envio",
    intro: "O custo de envio é uma das primeiras coisas que os compradores querem entender. Os preços da Basetao dependem do tamanho e peso do item, do destino e do método escolhido.",
    sections: [
      {
        heading: "Como os custos de envio são calculados",
        paragraphs: [
          "A maioria dos preços se baseia no peso real ou volumétrico do pacote e na distância. Itens maiores e mais pesados custam mais para enviar.",
          "O custo final costuma ser mostrado antes do pagamento, mas vale conferir se impostos ou taxas estão incluídos.",
        ],
      },
      {
        heading: "Comparar métodos de envio",
        paragraphs: [
          "Métodos econômicos são os mais baratos, mas lentos; os expressos são mais rápidos e caros. Os padrão equilibram custo e tempo.",
          "Escolher o método certo é um equilíbrio entre orçamento e rapidez. Sem pressa, o envio econômico economiza uma quantia apreciável.",
        ],
      },
      {
        heading: "Como reduzir custos",
        paragraphs: [
          "Consolidar vários itens em um envio costuma reduzir o custo por item. Esperar um pedido maior faz o envio econômico valer mais a pena.",
          "Compare o custo total — preço mais envio — em vez de só a tarifa de envio. Um item um pouco mais caro com envio barato pode ser melhor negócio.",
        ],
      },
    ],
    comparisonTitle: "Comparação de métodos de envio",
    comparisonHeaders: ["Método", "Custo", "Rapidez", "Ideal para"],
    comparisonRows: [
      ["Econômico", "O mais baixo", "O mais lento", "Orçamento, sem pressa"],
      ["Padrão", "Moderado", "Equilibrado", "A maioria dos pedidos"],
      ["Expresso", "O mais alto", "O mais rápido", "Itens urgentes ou valiosos"],
      ["Consolidado", "Mais barato por item", "Variável", "Vários itens de uma vez"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Por que os custos de envio variam tanto?", a: "O custo depende do peso e tamanho do pacote, do país de destino e do método escolhido." },
      { q: "É mais barato enviar vários itens juntos?", a: "Geralmente sim. Consolidar em um pacote costuma reduzir o custo por item." },
      { q: "Os impostos estão incluídos no preço de envio?", a: "Varia. Confira sempre o detalhamento do preço final para ver impostos e taxas." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-shipping-guide": {
    slug: "basetao-shipping-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20small%20cardboard%20parcel%20with%20a%20shipping%20label%20next%20to%20a%20delivery%20van%20model%20and%20a%20clipboard%20on%20a%20soft%20olive%20green%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-shipping-guide&orientation=landscape",
    title: "Guia de envio da Basetao: métodos, prazos e rastreio",
    cardDescription: "Um guia prático dos métodos de envio, prazos de entrega e rastreio da Basetao para você saber exatamente o que esperar.",
    metaDescription: "Entenda os métodos de envio, prazos e rastreio da Basetao. Aprenda a escolher o serviço certo e acompanhar seu pacote.",
    eyebrow: "Guia de envio",
    intro: "Entender o envio antes de pedir elimina grande parte do estresse da espera. Este guia cobre os métodos principais, prazos típicos e como rastrear seu pacote.",
    sections: [
      {
        heading: "Métodos de envio explicados",
        paragraphs: [
          "A Basetao envia por uma mistura de serviços econômicos, padrão e expressos. Cada um equilibra custo, rapidez e nível de rastreio.",
          "Sua escolha deve refletir a urgência e o quanto quer economizar. Para a maioria, um serviço padrão com rastreio é um bom meio-termo.",
        ],
      },
      {
        heading: "Prazos de entrega por método",
        paragraphs: [
          "Os prazos dependem do método e do destino. O envio econômico pode levar semanas; o expresso pode chegar em dias.",
          "Trate as estimativas como um guia, não uma promessa. Alfândega, feriados e clima podem adicionar tempo.",
        ],
      },
      {
        heading: "Como funciona o rastreio",
        paragraphs: [
          "Um número de rastreio permite acompanhar seu pacote do armazém à sua porta. A maioria dos serviços atualiza o status em pontos-chave.",
          "Se o rastreio parar, espere alguns dias antes de contatar, pois os escaneamentos internacionais às vezes atrasam.",
        ],
      },
    ],
    comparisonTitle: "Resumo da entrega",
    comparisonHeaders: ["Método", "Tempo típico", "Rastreio", "Bom saber"],
    comparisonRows: [
      ["Econômico", "2–6 semanas", "Limitado", "Opção mais barata"],
      ["Padrão", "1–3 semanas", "Bom", "Escolha equilibrada"],
      ["Expresso", "3–7 dias", "Detalhado", "Mais rápido e caro"],
      ["Correio", "Variável", "Porta a porta", "Muitas vezes o mais confiável"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Quanto tempo leva o envio da Basetao?", a: "Depende do método e destino, de alguns dias no expresso a várias semanas no econômico." },
      { q: "Receberei um número de rastreio?", a: "A maioria dos métodos padrão e expresso inclui rastreio. Os econômicos podem oferecer rastreio limitado." },
      { q: "E se meu pacote atrasar?", a: "Confira primeiro o status, depois contate o suporte se não houver atualização por muito tempo." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-coupon-discount-code": {
    slug: "basetao-coupon-discount-code",
    image:
      "https://readdy.ai/api/search-image?query=A%20pair%20of%20scissors%20and%20a%20discount%20gift%20tag%20with%20a%20receipt%20and%20a%20small%20stack%20of%20coins%20on%20a%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-coupon&orientation=landscape",
    title: "Cupom e código de desconto da Basetao: guia atualizado",
    cardDescription: "Onde encontrar cupons e códigos de desconto da Basetao, como usá-los e como evitar ofertas vencidas ou inválidas.",
    metaDescription: "Encontre e use cupons e códigos de desconto da Basetao. Aprenda de onde vêm, como aplicá-los e evitar ofertas vencidas.",
    eyebrow: "Economia",
    intro: "Cupons e códigos de desconto são uma forma simples de reduzir o custo total de um pedido. Este guia explica sua origem, uso e erros comuns.",
    sections: [
      {
        heading: "De onde vêm cupons e códigos",
        paragraphs: [
          "Os códigos costumam ser compartilhados por boletins, promoções sazonais e pelo site principal. Alguns valem para todo o site, outros para categorias ou mínimos específicos.",
          "Os códigos mais confiáveis vêm de fontes oficiais. Os de sites de terceiros devem ser conferidos, pois podem estar vencidos ou inválidos.",
        ],
      },
      {
        heading: "Como aplicar um código de desconto",
        paragraphs: [
          "A maioria dos códigos é inserida no checkout em um campo dedicado antes de confirmar. O desconto é então descontado do total.",
          "Leia primeiro as condições, pois muitos códigos exigem um valor mínimo ou excluem categorias. Aplicar errado pode fazê-lo parecer vencido.",
        ],
      },
      {
        heading: "Evitar ofertas vencidas ou inválidas",
        paragraphs: [
          "Confira sempre a data de validade e o valor mínimo antes de montar o carrinho em torno de um código. Um código vencido na semana passada não funciona esta semana.",
          "Se um código não aplicar, revise a grafia e as condições antes de assumir que é falso. Alguns descontos diferenciam maiúsculas ou são para novos clientes.",
        ],
      },
    ],
    comparisonTitle: "Tipos de desconto em resumo",
    comparisonHeaders: ["Tipo", "Como funciona", "Requisito típico", "Onde encontrar"],
    comparisonRows: [
      ["Percentual", "Reduz o total em um percentual", "Muitas vezes um mínimo", "Boletins e promoções"],
      ["Valor fixo", "Desconta uma soma do total", "Valor mínimo", "Campanhas sazonais"],
      ["Frete grátis", "Remove a tarifa de envio", "Pode exigir um mínimo", "Banners do site"],
      ["Oferta de categoria", "Aplica a uma categoria", "Específico da categoria", "Páginas de produto"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Onde encontro um código de desconto da Basetao?", a: "Confira o site principal, boletins e promoções sazonais. As fontes oficiais são as mais confiáveis." },
      { q: "Por que meu código não funciona?", a: "Pode estar vencido, exigir um mínimo ou estar limitado a categorias ou novos clientes." },
      { q: "Posso combinar vários cupons?", a: "Geralmente não. A maioria das lojas permite um código por pedido; escolha o que mais economiza." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-warehouse-guide": {
    slug: "basetao-warehouse-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20clean%20bright%20warehouse%20aisle%20with%20neatly%20stacked%20cardboard%20boxes%20on%20steel%20shelves%20and%20a%20soft%20stone%20color%20palette%2C%20natural%20overhead%20light%2C%20minimal%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-warehouse&orientation=landscape",
    title: "Guia do armazém da Basetao: como funciona o armazenamento",
    cardDescription: "Como o armazém da Basetao recebe, armazena e consolida seus itens antes de enviá-los a você.",
    metaDescription: "Descubra como o armazém da Basetao recebe, armazena e consolida seus itens, e como isso afeta o tempo e a precisão do envio.",
    eyebrow: "Armazém",
    intro: "O armazém é onde seus itens são recebidos, guardados e preparados para o envio. Entender essa etapa ajuda a decidir melhor sobre consolidação e entrega.",
    sections: [
      {
        heading: "Como o armazém recebe itens",
        paragraphs: [
          "Quando um vendedor envia um item, ele chega ao armazém e é registrado contra seu pedido. Isso confirma que o item certo chegou antes de ser guardado.",
          "Os registros de recebimento criam um rastro. Se algo faltar ou estiver errado, o registro do armazém é o primeiro lugar a olhar.",
        ],
      },
      {
        heading: "Armazenamento e consolidação",
        paragraphs: [
          "Os itens ficam guardados até você querer enviá-los. Se comprar de vários vendedores, o armazém pode consolidar tudo em um pacote, o que costuma baixar o custo.",
          "A consolidação é valiosa em pedidos internacionais, onde enviar vários pacotes pequenos separados custaria muito mais que uma caixa.",
        ],
      },
      {
        heading: "Preparar seu pedido para o envio",
        paragraphs: [
          "Antes do envio, a equipe confirma o conteúdo e prepara o pacote. Fotos e listas nessa etapa ajudam a detectar problemas evidentes cedo.",
          "Um pacote bem preparado chega menos danificado. Pergunte sobre a embalagem e fotos se enviar itens frágeis ou valiosos.",
        ],
      },
    ],
    comparisonTitle: "Fluxo do armazém",
    comparisonHeaders: ["Etapa", "O que acontece", "Por que importa"],
    comparisonRows: [
      ["Recebimento", "Itens registrados no pedido", "Confirma o item certo"],
      ["Armazenamento", "Itens guardados até o envio", "Permite esperar e combinar"],
      ["Consolidação", "Vários itens unidos", "Baixa o custo por item"],
      ["Embalagem", "Conteúdo conferido e embalado", "Reduz danos e erros"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Quanto tempo os itens ficam no armazém?", a: "Os prazos variam; confira a política atual para evitar taxas ou devoluções automáticas." },
      { q: "O que é consolidação?", a: "Une itens de vários vendedores em um pacote, o que geralmente reduz o custo total de envio." },
      { q: "O armazém inspeciona meus itens?", a: "Conferências básicas são comuns, mas o controle de qualidade detalhado costuma ser um passo à parte que você pode pedir." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-qc-guide": {
    slug: "basetao-qc-guide",
    image:
      "https://readdy.ai/api/search-image?query=Two%20hands%20inspecting%20a%20folded%20garment%20with%20a%20checklist%20clipboard%20and%20a%20measuring%20tape%20on%20a%20clean%20desk%2C%20soft%20sage%20green%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-qc&orientation=landscape",
    title: "Guia de controle de qualidade da Basetao: o QC explicado",
    cardDescription: "O que o controle de qualidade da Basetao realmente confere, como pedir fotos de QC e o que o processo pode ou não garantir.",
    metaDescription: "Entenda o controle de qualidade da Basetao — o que confere, como pedir fotos de QC e o que pode e não pode garantir.",
    eyebrow: "Controle de qualidade",
    intro: "O controle de qualidade, ou QC, é a etapa onde seus itens são inspecionados em busca de problemas evidentes antes do envio. É uma das ferramentas mais úteis para comprar com confiança.",
    sections: [
      {
        heading: "O que o controle de qualidade confere",
        paragraphs: [
          "O QC geralmente confere se o item corresponde à ficha, parece correto e não tem danos evidentes. Em roupas, pode incluir tamanho, cor e costuras básicas.",
          "O objetivo é detectar problemas claros antes de o item viajar pelo mundo. Não é um teste profundo, mas filtra muitos problemas comuns.",
        ],
      },
      {
        heading: "Como pedir fotos de QC",
        paragraphs: [
          "Muitos serviços permitem pedir fotos dos seus itens antes do envio. Elas mostram exatamente o que você receberá e confirmam a correspondência com a ficha.",
          "Revise as fotos com cuidado. Se algo parecer errado — cor errada, defeito visível ou estilo diferente — é muito mais fácil corrigir antes do envio.",
        ],
      },
      {
        heading: "O que o QC pode e não pode fazer",
        paragraphs: [
          "O QC detecta problemas visíveis, mas não pode testar durabilidade, toque do tecido ou qualidade a longo prazo. Pequenos defeitos ocultos podem passar.",
          "Use o QC como parte da decisão, não como garantia total. Combine-o com uma leitura atenta e uma boa escolha de vendedor.",
        ],
      },
    ],
    comparisonTitle: "Resumo do processo de QC",
    comparisonHeaders: ["Passo", "O que confere", "Limitação"],
    comparisonRows: [
      ["Correspondência", "Item vs. ficha", "Não confere detalhes ocultos"],
      ["Inspeção visual", "Danos ou defeitos evidentes", "Pequenos defeitos possíveis"],
      ["Tamanho / cor", "Especificações básicas", "O caimento depende de você"],
      ["Fotos", "O que você receberá", "Não é teste de durabilidade"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Vale a pena o controle de qualidade?", a: "Sim, sobretudo em roupas e itens de valor. Ele detecta muitos problemas evidentes antes do envio." },
      { q: "O QC garante um item perfeito?", a: "Não. Ele detecta problemas visíveis, mas não pode testar durabilidade, toque ou qualidade a longo prazo." },
      { q: "Como consigo fotos de QC?", a: "Peça-as pelo serviço ao fazer ou revisar seu pedido, se a opção estiver disponível." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-safety-guide": {
    slug: "basetao-safety-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20small%20padlock%20and%20a%20shield-shaped%20wooden%20token%20on%20a%20clean%20desk%20with%20a%20notebook%20and%20pen%2C%20soft%20warm%20gray%20background%2C%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-safety&orientation=landscape",
    title: "Guia de segurança e prevenção de golpes da Basetao",
    cardDescription: "Passos práticos para se manter seguro na Basetao — pagamentos seguros, detectar vendedores suspeitos e proteger sua conta.",
    metaDescription: "Mantenha-se seguro na Basetao com dicas anti-golpe: pagamentos seguros, detecção de vendedores suspeitos e proteção da sua conta.",
    eyebrow: "Segurança",
    intro: "Comprar com segurança é sobretudo questão de bons hábitos. Este guia cobre pagamentos seguros, como detectar vendedores suspeitos e proteger sua conta e seu dinheiro.",
    sections: [
      {
        heading: "Use métodos de pagamento seguros",
        paragraphs: [
          "Pague sempre com métodos reconhecidos e rastreáveis que ofereçam proteção ao comprador. Evite pagar diretamente a vendedores ou por serviços não rastreáveis.",
          "Se alguém pedir para pagar fora da plataforma ou por um método incomum, trate como sinal de alerta e se afaste.",
        ],
      },
      {
        heading: "Detectar vendedores suspeitos",
        paragraphs: [
          "Vendedores suspeitos costumam ter fichas vagas, preços irreais ou pressionam você a agir rápido. Um preço muito abaixo do mercado raramente é uma pechincha real.",
          "Confira os dados do vendedor, leia a descrição completa e busque informações coerentes. Vendedores confiáveis dão fichas claras e específicas.",
        ],
      },
      {
        heading: "Proteger sua conta",
        paragraphs: [
          "Use uma senha forte e única e ative as funções de segurança disponíveis. Nunca compartilhe suas credenciais ou códigos de uso único, nem com quem se diz suporte.",
          "Guarde pedidos, mensagens e pagamentos. Uma boa documentação facilita muito resolver uma disputa se algo der errado.",
        ],
      },
    ],
    comparisonTitle: "Lista de verificação de segurança",
    comparisonHeaders: ["Hábito", "Por que ajuda", "Sinal de alerta a evitar"],
    comparisonRows: [
      ["Pagamentos reconhecidos", "Proteção ao comprador", "Transferências diretas ou não rastreáveis"],
      ["Verificar o vendedor", "Responsabilidade", "Fichas vagas ou ausentes"],
      ["Ler descrições", "Evitar surpresas", "Preços irreais"],
      ["Guardar registros", "Disputas mais fáceis", "Apagar o histórico cedo"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Qual a forma mais segura de pagar na Basetao?", a: "Use métodos de pagamento reconhecidos e rastreáveis com proteção ao comprador. Evite transferências diretas." },
      { q: "Como detecto um vendedor suspeito?", a: "Procure fichas vagas, preços irreais e pressão para agir rápido. Os confiáveis dão detalhes claros." },
      { q: "O que fazer se eu suspeitar de um golpe?", a: "Interrompa a transação, denuncie o vendedor pela plataforma e contate o suporte com seus registros." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-buying-guide": {
    slug: "basetao-buying-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20paper%20shopping%20bag%20with%20a%20folded%20sweater%20and%20a%20pair%20of%20shoes%20beside%20a%20tablet%20on%20a%20warm%20blush%20pink%20background%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-buying&orientation=landscape",
    title: "Guia de compra da Basetao: de explorar ao checkout",
    cardDescription: "Um guia de compra da Basetao passo a passo — de escolher uma categoria a revisar fotos de QC e pagar com segurança.",
    metaDescription: "Um guia de compra da Basetao passo a passo sobre exploração, escolha de vendedor, controle de qualidade e checkout seguro.",
    eyebrow: "Guia de compra",
    intro: "Comprar na Basetao é simples quando você conhece o processo. Este guia acompanha você da primeira busca até um checkout seguro e confiante.",
    sections: [
      {
        heading: "Comece pela categoria certa",
        paragraphs: [
          "Explore primeiro por categoria — Calçados, Moletons, Camisetas, Jaquetas, Bolsas e mais — para transformar uma ideia ampla em uma lista concreta. Cada categoria abre uma visualização filtrada no site principal.",
          "Afastar cedo economiza tempo. Quanto mais claro o que você quer, mais fácil comparar as opções que combinam.",
        ],
      },
      {
        heading: "Escolha um vendedor e revise a ficha",
        paragraphs: [
          "Leia a descrição completa, confira as notas de tamanho e veja as fotos. Uma boa ficha é específica sobre material, medidas e conteúdo.",
          "Compare vários vendedores em vez de pular para o primeiro. Pequenas diferenças de preço ou detalhe podem importar.",
        ],
      },
      {
        heading: "Finalize com QC e checkout seguro",
        paragraphs: [
          "Peça fotos de controle de qualidade se disponível e revise-as antes do envio. Depois conclua o checkout com um método reconhecido e rastreável.",
          "Confira o método de envio, o prazo estimado e o custo total antes de confirmar. Alguns segundos a mais evitam a maioria dos problemas.",
        ],
      },
    ],
    comparisonTitle: "Processo de compra em resumo",
    comparisonHeaders: ["Passo", "O que você faz", "Dica-chave"],
    comparisonRows: [
      ["Explorar", "Escolher categoria", "Partir de uma ideia clara"],
      ["Selecionar", "Comparar vendedores", "Ler a descrição completa"],
      ["Revisar", "Conferir tamanhos e fotos", "Pedir QC se possível"],
      ["Pagar", "Pagar com segurança", "Confirmar total e envio"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Como começo a comprar na Basetao?", a: "Escolha uma categoria, explore a lista filtrada no site principal e compare algumas opções." },
      { q: "Devo pedir controle de qualidade?", a: "Sim, sobretudo em roupas ou itens de valor. Ajuda a detectar problemas evidentes antes do envio." },
      { q: "O que conferir antes de pagar?", a: "Confirme os detalhes, o método de envio, o prazo estimado e o custo total antes de finalizar." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },

  "basetao-seller-guide": {
    slug: "basetao-seller-guide",
    image:
      "https://readdy.ai/api/search-image?query=A%20minimal%20storefront%20concept%20with%20a%20small%20wooden%20sign%2C%20a%20handshake%20figurine%20and%20product%20cards%20on%20a%20warm%20khaki%20background%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20fresh%20airy%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=675&seq=art-seller&orientation=landscape",
    title: "Guia de vendedores da Basetao: encontrar vendedores confiáveis",
    cardDescription: "Como identificar vendedores confiáveis da Basetao, ler fichas com olhar crítico e comparar antes de se comprometer.",
    metaDescription: "Aprenda a encontrar vendedores confiáveis da Basetao, ler fichas com olhar crítico e comparar suas opções antes de comprar.",
    eyebrow: "Vendedores",
    intro: "O vendedor que você escolher tem grande impacto no que recebe. Este guia explica como identificar vendedores confiáveis e ler fichas com olhar crítico.",
    sections: [
      {
        heading: "O que torna um vendedor confiável",
        paragraphs: [
          "Vendedores confiáveis dão fichas completas com fotos claras, tamanhos precisos e descrições de material honestas. Respondem perguntas e oferecem preços realistas.",
          "A consistência é um sinal forte. Um vendedor com fichas detalhadas e corretas em vários produtos costuma ser mais seguro que um com descrições vagas.",
        ],
      },
      {
        heading: "Ler uma ficha com olhar crítico",
        paragraphs: [
          "Olhe além da primeira foto e leia cada detalhe. Confira a tabela de tamanhos, o material e se as fotos coincidem com a descrição.",
          "Desconfie de fichas só com fotos de banco, sem medidas ou que evitam responder perguntas básicas. A falta de detalhe costuma ser um sinal.",
        ],
      },
      {
        heading: "Comparar vendedores antes de se comprometer",
        paragraphs: [
          "Compare vários vendedores para o mesmo tipo de item. Olhe o preço, o prazo, a qualidade da ficha e as avaliações disponíveis.",
          "A opção mais barata nem sempre é a melhor. Um preço um pouco maior com um vendedor mais detalhado e atencioso pode evitar uma decepção.",
        ],
      },
    ],
    comparisonTitle: "Lista de avaliação de vendedores",
    comparisonHeaders: ["Fator", "Sinal confiável", "Sinal de alerta"],
    comparisonRows: [
      ["Detalhe da ficha", "Fotos e tamanhos claros", "Descrição vaga"],
      ["Preço", "Realista e coerente", "Suspeitamente baixo"],
      ["Comunicação", "Responde perguntas", "Evita ou ignora"],
      ["Fotos", "Coincidem com a descrição", "Só fotos de banco"],
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Como encontro um vendedor confiável da Basetao?", a: "Busque fichas completas com fotos claras, tamanhos precisos e descrições honestas. Compare antes de comprar." },
      { q: "Quais sinais indicam um mau vendedor?", a: "Fichas vagas, preços irreais, só fotos de banco e vendedores que evitam responder." },
      { q: "O vendedor mais barato é sempre o melhor?", a: "Não. Um preço um pouco maior com um vendedor mais detalhado costuma levar a uma melhor compra." },
    ],
    lastUpdated: "2026-08-19",
    author: "Equipe editorial da Basetao Spreadsheet",
  },
};